import React from 'react';

import cn from '@/utils/cn';

import FiguresPanel from './FiguresPanel';
import FixList from './FixList';
import MappingTable from './MappingTable';
import SpreadMap from './SpreadMap';
import StyleCoverage from './StyleCoverage';
import { calloutClass, toneText } from './tone';
import { DetectionReport } from './types';
import UnaccountedPanel from './UnaccountedPanel';

const StatTile = ({ detail, label, value }: { detail: string; label: string; value: string }) => (
  <div className="tw-box-border tw-rounded tw-border tw-border-solid tw-border-spa-border tw-p-3">
    <p className="tw-m-0 tw-text-xs tw-uppercase tw-tracking-wide tw-text-spa-secondary">{label}</p>
    <p className="tw-m-0 tw-mt-0.5 tw-text-xl tw-font-bold tw-tabular-nums tw-text-spa-heading">{value}</p>
    <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-spa-secondary">{detail}</p>
  </div>
);

const PanelHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="tw-m-0 tw-mt-6 tw-text-sm tw-font-bold tw-text-spa-heading">{children}</h3>
);

// The trust surface: what the tool read from the IDML and where every piece landed, shown before
// the diff so a "no change" verdict can be believed. Every error/warning item is actionable — by
// the sysadmin (review before pouring) or by the InDesign author (fix a style, re-export).
const DetectionView = ({ detected }: { detected: DetectionReport }) => {
  const { figures, fixes, mapping, spreads, stories, styles, tables, unclaimed, words } = detected;

  // A print_only row (e.g. a lesson opener with no online section) isn't an online section,
  // so it stays out of the "N of M online sections mapped" arithmetic.
  const onlineRows = mapping.filter(row => row.status !== 'print_only');
  const mapped = onlineRows.filter(row => row.status === 'mapped').length;
  const review = mapping.filter(row => row.status === 'review').length;
  const attention = unclaimed.length + review + styles.unrecognized.length + tables.length;
  const allClear = attention === 0;
  const tone = allClear ? 'success' : 'warning';

  const wordsAccounted = words.total - words.unclaimed - words.fragment;
  const wordsPct = words.total === 0 ? 100 : Math.round((wordsAccounted / words.total) * 100);

  return (
    <div className="tw-mt-8 tw-max-w-5xl">
      <div className={cn(calloutClass(tone), 'tw-rounded-lg tw-p-4', toneText[tone])} role="status">
        <p className="tw-m-0 tw-text-xl tw-font-bold">
          {allClear
            ? 'Good read — everything accounted for'
            : `${attention} item${attention === 1 ? '' : 's'} to review`}
        </p>
        <p className="tw-m-0 tw-mt-0.5 tw-text-sm">
          {mapped} of {onlineRows.length} online sections mapped to a print story.
        </p>
      </div>

      <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
        <StatTile
          detail={`${stories.matched} reading · ${stories.caption} captions · ${stories.vocab} vocab · `
            + `${stories.furniture + stories.fragment} furniture`}
          label="Print stories"
          value={String(stories.total)}
        />
        <StatTile
          detail={`${wordsAccounted} of ${words.total} words`}
          label="Words accounted"
          value={`${wordsPct}%`}
        />
        <StatTile
          detail={`${styles.unrecognized.length} unrecognized style${styles.unrecognized.length === 1 ? '' : 's'}`}
          label="Style coverage"
          value={`${Math.round(styles.coverage * 100)}%`}
        />
        <StatTile
          detail={`${figures.captioned} paired with a caption`}
          label="Figures"
          value={String(figures.total)}
        />
      </div>

      <PanelHeading>Print file check</PanelHeading>
      <FixList fixes={fixes} />

      {spreads.length > 0 && (
        <>
          <PanelHeading>Spread map — every frame, color-coded</PanelHeading>
          <SpreadMap spreads={spreads} />
        </>
      )}

      <PanelHeading>Print story → online section</PanelHeading>
      <MappingTable mapping={mapping} />

      <PanelHeading>Style coverage — the authoring contract, scored</PanelHeading>
      <StyleCoverage coverage={styles.coverage} unrecognized={styles.unrecognized} />

      <PanelHeading>Figures &amp; captions — paired by page geometry</PanelHeading>
      <FiguresPanel figures={figures} />

      <PanelHeading>Unaccounted content — what we found but don’t understand</PanelHeading>
      <UnaccountedPanel tables={tables} unclaimed={unclaimed} words={words} />
    </div>
  );
};

export default DetectionView;
